import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

/**
 * Light / dark mode for the CRM.
 *
 * Each agent picks their own mode in Settings > General > Appearance. The choice
 * is stored per browser in localStorage (no backend column, so nothing to
 * migrate) and applied by putting the `dark` class on <html>, which
 * src/styles/theme-dark.css keys off. public/index.html sets the same class
 * before React mounts so the page never flashes white on the way in.
 */

export const THEME_STORAGE_KEY = "collings-crm-theme";

export const THEMES = {
  LIGHT: "light",
  DARK: "dark",
  SYSTEM: "system",
};

/**
 * Client-facing routes. These pages are emailed and SMSed to buyers and vendors
 * (CMA reports, signing pages, OFI check-in, opt-out links) and are rendered on
 * whatever device opens the link, so an agent's personal dark mode must never
 * follow them there - they stay light, always.
 */
const PUBLIC_PATH_PREFIXES = [
  "/cma/",
  "/market-update/",
  "/esign/sign/",
  "/ofi-checkin/",
  "/property-view-listing",
  "/property-voice-agent",
  "/contact-agent",
  "/notifications/sms/stop/",
  "/notifications/email/unsubscribe",
  "/s/",
  "/l/",
  "/k/",
];

export const isPublicPath = (pathname = "") =>
  PUBLIC_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));

const prefersDark = () => {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch (e) {
    return false;
  }
};

export const readStoredTheme = () => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === THEMES.DARK || stored === THEMES.LIGHT || stored === THEMES.SYSTEM) {
      return stored;
    }
  } catch (e) {
    /* private browsing / storage disabled - fall through to the default */
  }
  return THEMES.LIGHT;
};

const ThemeContext = createContext({
  theme: THEMES.LIGHT,
  isDark: false,
  setTheme: () => {},
  toggleTheme: () => {},
});

export const ThemeProvider = ({ children }) => {
  const location = useLocation();
  const [theme, setThemeState] = useState(readStoredTheme);
  const [systemIsDark, setSystemIsDark] = useState(prefersDark);

  // Keep "Match my device" honest when the OS flips between light and dark.
  useEffect(() => {
    let media;
    try {
      media = window.matchMedia("(prefers-color-scheme: dark)");
    } catch (e) {
      return undefined;
    }
    const onChange = (event) => setSystemIsDark(event.matches);
    // Safari < 14 only has the deprecated listener API.
    if (media.addEventListener) media.addEventListener("change", onChange);
    else media.addListener(onChange);
    return () => {
      if (media.removeEventListener) media.removeEventListener("change", onChange);
      else media.removeListener(onChange);
    };
  }, []);

  const wantsDark = theme === THEMES.DARK || (theme === THEMES.SYSTEM && systemIsDark);
  const onPublicPage = isPublicPath(location.pathname);
  const isDark = wantsDark && !onPublicPage;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", isDark);
    // Lets the browser paint chrome (address bar, form widgets) to match.
    root.style.colorScheme = isDark ? "dark" : "light";
  }, [isDark]);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch (e) {
      /* the class is still applied for this session, it just won't persist */
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(wantsDark ? THEMES.LIGHT : THEMES.DARK);
  }, [setTheme, wantsDark]);

  const value = useMemo(
    () => ({ theme, isDark, wantsDark, setTheme, toggleTheme }),
    [theme, isDark, wantsDark, setTheme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
